import { firstValueFrom } from 'rxjs';
import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import * as moment from 'moment-timezone';
import liff from '@line/liff';
import { environment } from '../../environments/environment';

@Injectable({ providedIn: 'root' })
export class LiffService {
  private readonly http = inject(HttpClient);
  public isInit = false;
  public liff = liff;
  public profile: LineUserProfile = {
    userId: '',
    displayName: '',
    pictureUrl: '',
    statusMessage: '',
  };

  public initCount = 0;

  async initLiff(liffId: string, site: string) {
    ++this.initCount;
    console.log('line =>', liffId, site);
    console.log('isInClient =>', this.isInClient());
    try {
      // if (!this.isInit && this.initCount < 2) {

      // }
      console.log('***initLiff', this.initCount);
      await this.liff.init({
        liffId,
      });
      this.isInit = true;
      console.log('****isInit', this.isInit, liffId);

      if (this.liff.isLoggedIn()) {
        this.profile = await this.liff.getProfile();
        console.log('profile =>', this.profile);
        const decodedIDToken: any = liff.getDecodedIDToken();
        console.log('decodedIDToken =>', decodedIDToken);
      }
    } catch (err: any) {
      console.error(err);
      let errorJSON = {};
      try {
        errorJSON = JSON.parse(JSON.stringify(err));
      } catch (err) {
        console.error(err);
      }

      if (this.initCount < 2) {
        this.initCount++;
        console.log('initCount end=>', this.initCount);
      } else {
        throw new Error('此APP尚未啟用');
      }
    }
  }

  /**
   * 判斷是否為本機開發模式
   */
  public isDevMode() {
    return window.location.host.includes('localhost:');
  }

  /**
   * 登入 LIFF 帳號
   * @param redirectUri 登入後跳轉的網址
   */
  public login(redirectUri = window.location.href) {
    this.liff.login({
      redirectUri,
    });
  }

  /**
   * 登出 LIFF 帳號
   */
  public logout() {
    this.liff.logout();
  }

  /**
   * 判斷登入狀態
   */
  public isLoggedIn() {
    return this.liff.isLoggedIn();
  }

  /**
   * 判斷環境 "ios" | "android" | "web"
   */
  public getOS() {
    try {
      return this.liff.getOS();
    } catch (err) {
      return '';
    }
  }

  /**
   * 判斷LIFF的版本
   */
  public getVersion() {
    try {
      return this.liff.getVersion();
    } catch (err) {
      return '';
    }
  }

  /**
   * 判斷LIFF的版本
   */
  public getLineVersion() {
    try {
      return this.liff.getLineVersion();
    } catch (err) {
      return '';
    }
  }

  /**
   * 判斷是否為原生LIFF的環境
   */
  public isInClient() {
    try {
      return this.liff.isInClient();
    } catch (err) {
      return false;
    }
  }

  /**
   * 取得 AccessToken
   */
  public getAccessToken() {
    try {
      return this.liff.getAccessToken();
    } catch (err) {
      return '';
    }
  }

  /**
   * 取得裝置語系
   */
  public getLanguage() {
    try {
      return this.liff.getLanguage();
    } catch (err) {
      return '';
    }
  }

  /**
   * 發送並紀錄 User Log
   * @param site 必填 站台名稱
   * @param userId 必填 使用者的 userId
   * @param action 必填 事件名稱;
   * @param label 選填 自訂標籤/Key值;
   * @param value 選填 key/標籤
   * @param payload 選填 key/標籤
   */
  async log(
    site: string,
    userId: string,
    action: string,
    label?: string | number,
    value?: string | number,
    payload?: any,
    category?: 'LineLiff' | 'WebSite' | 'LineApp'
  ) {
    try {
      await firstValueFrom(
        this.http.post(`${environment.cloudRun.api}/log`, {
          site,
          userId,
          category: category || 'LineLiff',
          action,
          label,
          value,
          payload,
        })
      );
    } catch (err) {
      console.error(err);
    }
  }

  async getClientIP(): Promise<string> {
    try {
      const result = (await firstValueFrom(this.http.get(`${environment.cloudRun.api}/get-client-ip`))) as any;
      return result.IP || '';
    } catch (err) {
      console.error(err);
      return '';
    }
  }

  async getServerTimestamp(timezone = 'Asia/Taipei'): Promise<Date> {
    const result = (await firstValueFrom(this.http.get(`${environment.cloudRun.api}/get-server-timestamp`))) as any;
    return moment.tz(result.timestamp || '', timezone).toDate();
  }

  /**
   * 查詢該 site 站台的 botId 、Liff ID 及 公開資料
   * @param site 站台名稱
   */
  async querySiteProfile(site: string): Promise<{
    site: string;
    botId: string;
    pictureUrl: string;
    displayName: string;
    liff: LiffConfig;
    accountId: string;
    provider: string;
  }> {
    const result: any = await firstValueFrom(this.http.post(`${environment.cloudRun.api}/querySiteConfig`, { site }));
    return result;
  }

  /**
   * LIFF 於開啟並初始化時，送出 LiffOpen Event
   * @param liffId
   * @param site
   */
  private liffOpenLog(liffId: string, site?: string) {
    if (this.profile.userId) {
      const url = new URL(window.location.href);
      let targetSite = '';
      try {
        targetSite = site || url.searchParams.get('site') || url.pathname.split('/')[2] || 'noSite';
      } catch (err) {
        console.error(err);
        targetSite = 'noSite';
      }
      this.log(
        targetSite,
        this.profile.userId,
        'liffOpen',
        liffId,
        window.navigator.userAgent,
        window.location.href || ''
      );
    }
  }

  /**
   * 若為手機則強制使用原生 LIFF 分享
   * @param messagea
   */
  async forcedLiffShare(site: string, message: any[]) {
    try {
      if (this.getOS() === 'android' || this.getOS() === 'ios') {
        // 若使用手機
        if (this.isInClient()) {
          // 已是原生 LIFF 環境
          await liff.shareTargetPicker(message);
        } else {
          // 手機的 LINE 瀏覽器 或 外部瀏覽器
          const siteProfile = await this.querySiteProfile(site);
          const encodeText = encodeURIComponent(JSON.stringify(message));
          const httpReferer = encodeURIComponent(window.location.href);
          const shareLiffUrl = `https://liff.line.me/${siteProfile.liff.shareTargetPickerLiffID}?site=${site}&messageEncode=${encodeText}`;
          window.location.href = shareLiffUrl;
        }
      } else {
        // 桌機 直接使用 SDK 分享
        if (this.isLoggedIn()) {
          await liff.shareTargetPicker(message);
        } else {
          this.login();
        }
      }
    } catch (error: any) {
      alert(error.message);
    }
  }
  async close() {
    await liff.closeWindow();
  }

  /** 使用 LIFF 開啟外部頁面；SDK 尚未就緒時退回瀏覽器新分頁。 */
  openExternalWindow(url: string) {
    const normalizedUrl = url.trim();

    if (!normalizedUrl) {
      return;
    }

    try {
      this.liff.openWindow({
        url: normalizedUrl,
        external: false,
      });
    } catch (error) {
      console.error('LIFF openWindow failed, fallback to window.open.', error);
      window.open(normalizedUrl, '_blank', 'noopener,noreferrer');
    }
  }
}
